import {
  AccountId,
  ContractId,
  TokenId,
  TransactionId, 
} from "@hashgraph/sdk";
import { Stack, Box } from "@mui/system"; 
import { ContractFunctionParameterBuilder } from "../services/wallets/contractFunctionParameterBuilder";
import { useWalletInterface } from "../services/wallets/useWalletInterface";
import { useEffect, useState } from "react";
import { Button, Typography, CircularProgress, TextField } from "@mui/material"; 
import SendIcon from '@mui/icons-material/Send';
import AddLinkIcon from '@mui/icons-material/AddLink'; 
import ReplayIcon from '@mui/icons-material/Replay'; 
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// --- Imports all from mirrorUtils ---
import {
  isAssociatedWithToken,
} from "../services/mirrorUtils";

// --- STATE DEFINITIONS ---
type MintStatus = "IDLE" | "WALLET_APPROVE" | "FINDING_NUMBER" | "SUCCESS" | "ERROR";

export default function Home() {
  const { walletInterface, accountId } = useWalletInterface();
  
  const [mintStatus, setMintStatus] = useState<MintStatus>("IDLE");
  
  // New State for Results
  const [mintResult, setMintResult] = useState<{serial: number, roll: number} | null>(null);
  
  const [errorMessage, setErrorMessage] = useState(""); 
  const [referrerId, setReferrerId] = useState(""); 
  
  // --- Association States ---
  const [isAssociated, setIsAssociated] = useState(false);
  const [checkingAssociation, setCheckingAssociation] = useState(false);
  
  // --- States for the Association Action ---
  const [associating, setAssociating] = useState(false);
  const [associateError, setAssociateError] = useState("");











  const tokenId = "0.0.10158669"; 
  const contractId = "0.0.10158659"; 

  // --- HELPER: Map Roll to Image/Name ---
  // --- HELPER: Map Roll to Image/Name ---
  const getGiftData = (roll: number) => {
    // Reward Logic
    if (roll === 101) return { name: "an exotic\nReferral Rhombus!", image: "rhombus.png" };
    if (roll === 102) return { name: "an exotic\nReferred Ruby!", image: "ruby.png" };
    if (roll === 103) return { name: "an exotic\nChristmas Cube!", image: "cube.png" };
    // Standard Probability Logic
    if (roll === 1) return { name: "a legendary\nGargantuan Gift!!", image: "gargantuan.png" };
    if (roll <= 3) return { name: "a legendary\nSpherical Surprise!", image: "sphere.png" };
    if (roll <= 8) return { name: "an epic\nSensational Star!", image: "star.png" };
    if (roll <= 13) return { name: "an epic\nPyramid Present!", image: "pyramid.png" };
    if (roll <= 20) return { name: "a rare\nTrapezoidal Treat!", image: "trapezoid.png" };
    if (roll <= 30) return { name: "a rare\nFestive Flat!", image: "flat.png" };
    if (roll <= 50) return { name: "an uncommon\nBasic Box.", image: "box.png" };
    
    return { name: "a common\nLump of Coal.", image: "coal.png" };
  };


  // --- Helper: Get EVM Address from Mirror Node ---
  const getEvmAddressFromMirror = async (idString: string): Promise<string> => {
    if (!idString || idString.trim() === "" || idString === "0.0.0") {
      return "0x0000000000000000000000000000000000000000";
    }

    try {
      const url = `https://mainnet.mirrornode.hedera.com/api/v1/accounts/${idString}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.evm_address) {
        return data.evm_address;
      } else {
        const solidityAddress = AccountId.fromString(idString).toSolidityAddress();
        return `0x${solidityAddress}`;
      }
    } catch (e) {
      console.error("Error resolving referrer:", e);
      return "0x0000000000000000000000000000000000000000";
    }
  };

  // --- Helper: Fetch Result from Mirror Node ---
  const checkMirrorNodeForYield = async (txId: TransactionId): Promise<{ status: "PENDING" | "REVERTED" | "SUCCESS", serial: number | null, roll: number | null }> => {
    try {
      const txIdString = txId.toString();
      const [accountPart, timePart] = txIdString.split('@');
      const [seconds, nanos] = timePart.split('.');
      const formattedTxId = `${accountPart}-${seconds}-${nanos}`;
      
      const url = `https://mainnet.mirrornode.hedera.com/api/v1/contracts/results/${formattedTxId}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        return { status: "PENDING", serial: null, roll: null };
      }
      
      const data = await response.json();
      
      if (data.result !== "SUCCESS") {
         return { status: "REVERTED", serial: null, roll: null };
      }

      if (data.logs && data.logs.length > 0) {
         // Look for the NftMinted event (It has 3 data fields, so reasonably long data)
         const targetLog = data.logs.find((log: any) => 
            log.data && log.data.length > 66 
         );

         if (targetLog) {
            const rawData = targetLog.data.replace("0x", "");
            
            // Decode NftMinted Data:
            // 0-64: Serial (int64)
            // 64-128: Roll (uint256)
            // 128-192: activeReferrer (address)
            
            const serialHex = rawData.substring(0, 64);
            const serial = parseInt(serialHex, 16);

            const rollHex = rawData.substring(64, 128);
            const roll = parseInt(rollHex, 16);

            return { status: "SUCCESS", serial: serial, roll: roll };
         }
      }
      
      return { status: "SUCCESS", serial: null, roll: null };

    } catch (e) {
      console.error("Error fetching from mirror node:", e);
      return { status: "PENDING", serial: null, roll: null };
    }
  };

  // --- NEW: Check Previous Calls for Stored Referrer ---
  const checkStoredReferral = async (userAccountId: string) => {
    if (!contractId || !userAccountId) return null;

    try {
        console.log(`Checking stored referrer for user: ${userAccountId}`);

        // 1. Query Last Contract Result for this User
        const listUrl = `https://mainnet.mirrornode.hedera.com/api/v1/contracts/${contractId}/results?from=${userAccountId}&order=desc&limit=1`;
        const listResp = await fetch(listUrl);
        const listData = await listResp.json();

        // If user has never called contract, return null
        if (!listData.results || listData.results.length === 0) {
            console.log("-> No previous calls found.");
            return null;
        }

        const latestResult = listData.results[0];
        const txHash = latestResult.hash; // Get hash to query full details

        // 2. Query Full Result Details (to ensure we get logs)
        const detailUrl = `https://mainnet.mirrornode.hedera.com/api/v1/contracts/results/${txHash}`;
        const detailResp = await fetch(detailUrl);
        const detailData = await detailResp.json();

        if (!detailData.logs || detailData.logs.length === 0) {
            console.log("-> Last call had no logs.");
            return null;
        }

        // 3. Parse Logs to find NftMinted and extract activeReferrer
        // Event Signature: NftMinted(address indexed recipient, int64 serialNumber, uint256 rollResult, address activeReferrer)
        // Data Structure (Non-indexed fields): 
        //  [0-32 bytes] serial
        //  [32-64 bytes] roll
        //  [64-96 bytes] activeReferrer
        
        // Iterate reverse to get the last event if multiple occurred
        const logs = detailData.logs.reverse();

        for (const log of logs) {
            const rawData = log.data.replace("0x", "");
            
            // We need at least 96 bytes (192 hex chars)
            if (rawData.length >= 192) {
                // Extract 3rd 32-byte chunk (chars 128 to 192)
                const referrerHex = rawData.substring(128, 192);
                const referrerAddress = "0x" + referrerHex.slice(-40); // Last 20 bytes

                // Check for Empty Address
                if (referrerAddress === "0x0000000000000000000000000000000000000000") {
                    console.log("-> Stored referrer was blank (0x0).");
                    return ""; // Return empty string to clear field
                }

                // 4. Resolve EVM to Hedera ID (0.0.x) for UI
                console.log(`-> Found stored referrer EVM: ${referrerAddress}`);
                const accUrl = `https://mainnet.mirrornode.hedera.com/api/v1/accounts/${referrerAddress}`;
                const accResp = await fetch(accUrl);
                const accData = await accResp.json();

                if (accData.account) {
                    console.log(`-> Resolved to Hedera ID: ${accData.account}`);
                    return accData.account; 
                }
            }
        }
        
        return null;

    } catch (e) {
        console.error("Error checking stored referral:", e);
        return null;
    }
  };

  // --- Effect: Populate Referrer (URL or Contract History) ---
  useEffect(() => {
    const initReferrer = async () => {
        // 1. Check URL first (Highest Precedence)
        if (typeof window !== "undefined") {
            const params = new URLSearchParams(window.location.search);
            const urlId = params.get("id");
            
            if (urlId) {
                console.log("Referrer found in URL:", urlId);
                setReferrerId(urlId);
                return; // Stop here, URL wins
            }
        }

        // 2. If no URL, check Contract History
        if (accountId) {
            // Note: We pass the Hedera Account ID (0.0.x) to the mirror node query
            const storedReferrer = await checkStoredReferral(accountId);
            
            // Only update if we found a valid ID or explicitly found a blank (empty string)
            if (storedReferrer !== null) {
                setReferrerId(storedReferrer);
            }
        }
    };

    initReferrer();
  }, [accountId]); 

  // --- Effect to check association on load/login ---
  useEffect(() => {
    const checkAssociation = async () => {
      if (!accountId) return;
      
      setCheckingAssociation(true);
      try {
        const associated = await isAssociatedWithToken(accountId, tokenId);
        setIsAssociated(associated);
      } catch (error) {
        console.error("Error checking association:", error);
      } finally {
        setCheckingAssociation(false);
      }
    };

    checkAssociation();
  }, [accountId, tokenId]);

  // --- Handle Token Association ---
  const handleAssociate = async () => {
    if (!walletInterface) return;
    
    setAssociating(true);
    setAssociateError(""); 

    try {
      const tokenIdObj = TokenId.fromString(tokenId);

      await walletInterface.associateToken(tokenIdObj);
      
      await new Promise(r => setTimeout(r, 4000));
      
      const associated = await isAssociatedWithToken(accountId, tokenId);
      setIsAssociated(associated);
      
      if (!associated) {
          setAssociateError("Please try refreshing in a moment.");
      }

    } catch (err: any) {
      console.error("Association failed:", err);
      setAssociateError(err.message || "Association failed");
    } finally {
      setAssociating(false);
    }
  };

  // --- MAIN CONTRACT CALL ---
  const handleCallContract = async () => {
    if (!walletInterface) return;
    
    setMintStatus("WALLET_APPROVE");
    setMintResult(null); 
    setErrorMessage("");
    
    const params = new ContractFunctionParameterBuilder();

    // Convert input (0.0.x) to EVM for the contract call
    const referrerEvm = await getEvmAddressFromMirror(referrerId);
    console.log("Using Referrer Address:", referrerEvm);
    
    params.addParam({
        type: "address",
        name: "referrer",
        value: referrerEvm
    });

    const gasLimit = 400_000; 

    try {
      const txId = await walletInterface.executeContractFunction(
        ContractId.fromString(contractId),
        "mintRandom", 
        params,
        gasLimit
      );

      if (txId) {
        console.log("Transaction submitted:", txId.toString());
        setMintStatus("FINDING_NUMBER");

        let finalOutcome = false;

        for (let i = 0; i < 20; i++) { 
            const result = await checkMirrorNodeForYield(txId);
            
            if (result.status === "REVERTED") {
                setErrorMessage("Error: Transaction Reverted. Tgifts ended in 2025.");
                setMintStatus("ERROR");
                finalOutcome = true;
                break;
            }
            
            if (result.status === "SUCCESS") {
                await new Promise(r => setTimeout(r, 500)); 

                if (result.serial !== null && result.roll !== null) {
                    setMintResult({ serial: result.serial, roll: result.roll });
                    setMintStatus("SUCCESS");
                    finalOutcome = true;
                    break;
                } else {
                    setErrorMessage("Minted, but couldn't read the result logs.");
                    setMintStatus("ERROR");
                    finalOutcome = true;
                    break;
                }
            }
            await new Promise(r => setTimeout(r, 1000)); 
        }

        if (!finalOutcome) {
            setErrorMessage("Error: Mirror node timed out. Please check your wallet history.");
            setMintStatus("ERROR");
        }

      } else {
        setMintStatus("IDLE");
      }
    } catch (err) {
      console.error("Contract call failed:", err);
      setErrorMessage("Error: Transaction cancelled or failed.");
      setMintStatus("ERROR"); 
    } 
  };

  return (
    <Stack alignItems="center" spacing={4} p={4} maxWidth={600} mx="auto">
      <Typography variant="h4" color="white" align="center">
        Minting TOZM Gifts Ended in 2025
      </Typography>

      {!accountId ? (
        <Typography color="white">
           Connect your wallet through the button above.
        </Typography>
      ) : (
        <>
            {/* ------------------------------------------------------------- */}
            {/* VIEW: SUCCESS */}
            {/* ------------------------------------------------------------- */}
            {mintStatus === "SUCCESS" && mintResult && (
                <Stack alignItems="center" spacing={2}>
                  {(() => {
                    const gift = getGiftData(mintResult.roll);
                    return (
                        <>
<Typography
  color="white"
  variant="h5"
  align="center"
  sx={{ whiteSpace: "pre-line" }}
>
  You got {gift.name}
</Typography>
                            
                            {/* IMAGE DISPLAY */}
                            <Box 
                                component="img"
                                src={`/${gift.image}`} // Assuming images are in public root
                                alt={gift.name}
                                sx={{
                                    width: 400,
                                    height: 400,
                                    objectFit: 'contain',
                                    borderRadius: 4,
                                    boxShadow: '0px 0px 20px rgba(255,255,255,0.2)',
                                    bgcolor: 'rgba(0,0,0,0.3)'
                                }}
                            />

                            <Typography color="#90caf9" variant="caption" align="center">
                                Serial #{mintResult.serial}
                            </Typography>
                        </>
                    );
                  })()}
                  
                  <Button
                    variant='contained'
                    startIcon={<ReplayIcon />}
                    onClick={handleCallContract}
                    sx={{ mt: 2 }}
                  >
                    Mint another Tgift!
                  </Button>
                </Stack>
            )}

            {/* ------------------------------------------------------------- */}
            {/* VIEW: ERROR */}
            {/* ------------------------------------------------------------- */}
            {mintStatus === "ERROR" && (
                <>
                   <Stack direction="column" alignItems="center" spacing={2} sx={{ border: '1px solid #f44336', p: 3, borderRadius: 2, bgcolor: 'rgba(244, 67, 54, 0.1)' }}>
                      <ErrorOutlineIcon color="error" sx={{ fontSize: 40 }}/>
                      <Typography color="error" variant="body1" align="center" fontWeight="bold">
                        {errorMessage || "An unknown error occurred."}
                      </Typography>
                      
                      <Button
                        variant='contained'
                        color="primary"
                        startIcon={<ReplayIcon />}
                        onClick={handleCallContract}
                      >
                        Try Again
                      </Button>
                   </Stack>
                </>
            )}

            {/* ------------------------------------------------------------- */}
            {/* VIEW: ACTIVE PROCESS STATES */}
            {/* ------------------------------------------------------------- */}
            {(mintStatus === "IDLE" || mintStatus === "WALLET_APPROVE" || mintStatus === "FINDING_NUMBER") && (
              <Stack direction="column" gap={2} alignItems="center" minHeight={100} justifyContent="center" width="100%">
                
                {checkingAssociation ? (
                   <CircularProgress size={24} color="inherit" />
                ) : !isAssociated ? (
                   // NOT ASSOCIATED
                   <>
                      <Typography color="warning.main" align="center" variant="body2">
                        You need to be associated with {tokenId} to mint.
                      </Typography>

                      <Button 
                        variant="contained"
                        color="secondary" 
                        onClick={handleAssociate}
                        disabled={associating}
                        startIcon={associating ? <CircularProgress size={20} color="inherit" /> : <AddLinkIcon />}
                      >
                        Associate Token
                      </Button>
                      
                      {associateError && (
                        <Typography variant="caption" color="error">
                           {associateError}
                        </Typography>
                      )}
                   </>
                ) : (
                   // ASSOCIATED -> INPUT & MINT FLOW
                   <>
                      {/* 0. IDLE (Input + Button) */}
                      {mintStatus === "IDLE" && (
                          <Stack direction="column" gap={3} width="100%">

<Typography variant="body2" color="#e0e0e0" align="center">
                                Tgifts can no longer be minted; however, you can still mint previously earned Referral Rhombuses.
                              </Typography>

                              <TextField
                                label="Referrer ID (Optional)"
                                placeholder="0.0.xxxxx"
                                variant="outlined"
                                value={referrerId}
                                onChange={(e) => setReferrerId(e.target.value)}
                                sx={{ 
                                    input: { color: 'white' }, 
                                    label: { color: 'grey' },
                                    fieldset: { borderColor: 'grey' }
                                }}
                                fullWidth
                              />

                              <Button 
                                variant="contained" 
                                size="large"
                                onClick={handleCallContract}
                                startIcon={<SendIcon />}
                                fullWidth
                              >
                                Mint a Tgift!
                              </Button>
                          </Stack>
                      )}

                      {/* 1. WALLET APPROVAL */}
                      {mintStatus === "WALLET_APPROVE" && (
                          <Typography variant="body1" color="white" sx={{ fontStyle: 'italic' }}>
                              Approve the transaction in your wallet...
                          </Typography>
                      )}

                      {/* 2. FINDING NUMBER */}
                      {mintStatus === "FINDING_NUMBER" && (
                          <Stack alignItems="center" spacing={1}>
                              <CircularProgress size={20} color="inherit" />
                              <Typography variant="body1" color="white" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
                                  Minting and checking to see what you got...
                              </Typography>
                          </Stack>
                      )}
                   </>
                )}
              </Stack>
            )}
        </>
      )}

<Typography color="white">
        Need help? See the{" "}
        <a
          href="https://tozm.io/?month12"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#90caf9" }}
        >
          December 2025 info page
        </a>
{", "}
        <a
          href="https://discord.gg/SN8zxugwgg"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#90caf9" }}
        >
          Discord
        </a>
{", or "}
        <a
          href="https://x.com/tozmdotio"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#90caf9" }}
        >
          X
        </a>
        .
        <br />
        <br />
        By using this dApp you signify your agreement to our{" "}
        <a
          href="https://tozm.io/?page=terms"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#90caf9" }}
        >
          Terms
        </a>
        .
      </Typography>

    </Stack>
  );
}